import { isNil } from 'ramda';
import { ofType } from 'redux-observable';
import { of } from 'rxjs';
import { catchError, filter, map, mergeMap, retry, switchMap } from 'rxjs/operators';
import { actions } from '.';
import { concatLatest } from '../../../Lib/Rxjs/operators';
import webApi from '../../../Lib/WebApi';
import { SyncStatus, syncTarget } from '../../Core/legacyDataSync';
import { errorToString, sanitizeErrorObject } from '../../Helpers/errorHelpers';
import { addErrorNotice, callApi, catchWithAuthErrors } from '../../Helpers/rxjsHelpers';
import { adminPageApiCalls } from './apiCalls';

export const init = (action$) =>
  action$.pipe(
    ofType(actions.init.toString()),
    switchMap(() => callApi(webApi.admin.license.products.get())),
    map((res) =>
      actions.initResolve({
        licensedProducts: res.Body.Products?.filter((p) => p.Licensed).map((p) => p.Name),
        agsLicensed: res.Body.AgsLicensed,
      })
    ),
    catchWithAuthErrors((err) =>
      of(actions.initReject(), addErrorNotice('Failed to initialise administration page.', err))
    )
  );

export const initPage = (action$, state$) =>
  action$.pipe(
    ofType(actions.initPage.toString()),
    switchMap(({ payload: { adminPage, elementId } = {} }) => getApiCall(adminPage, elementId)(state$.value)),
    map((data) => actions.initPageResolve({ data })),
    catchError((err) => of(actions.initPageReject(), addErrorNotice('Failed to initialise administration page.', err)))
  );

export const onDataChange = (action$) =>
  action$.pipe(
    ofType(
      // After admin section is loaded, run a sync to ensure things are in correct state.
      actions.initResolve.toString(),

      // On sync completion, run sync again to make sure new changes are synchronised.
      actions.syncDataResolve.toString(),

      // Sync whenever a value changes.
      actions.setValue.toString()
    ),
    filter(({ payload: { sync } = {} }) => !!sync), // only when sync is set to true
    map(() => actions.syncData())
  );

const getApiCall = (adminPage, elementId) =>
  isNil(elementId) ? adminPageApiCalls(adminPage).get : adminPageApiCalls(adminPage).getIndividual(elementId);
const putApiCall = (adminPage, elementId) =>
  isNil(elementId) ? adminPageApiCalls(adminPage).put : adminPageApiCalls(adminPage).putIndividual(elementId);

/**
 * This is our main synchronisation epic, that is responsible for keeping the server up-to-date with the clients changes.
 * This runs like a service, continually (once at a time) updating the server state to look like the client state.
 */
export const syncData = (action$, state$) =>
  action$.pipe(
    ofType(actions.syncData.toString()),
    concatLatest(({ payload: { force } = {} }) =>
      syncTarget(state$.value.admin, force, ({ updateModel }) => {
        const adminPage = state$.value.admin.adminPage;
        const elementId = state$.value.admin.elementId;
        const optionalPut = updateModel ? putApiCall(adminPage, elementId)(updateModel) : of(1);
        return optionalPut.pipe(mergeMap(() => getApiCall(adminPage, elementId)(state$.value)));
      })
    ),
    mergeMap(({ status, syncPatch, error, recoveryError }) => {
      const epicActionsOut = [];
      if (error) epicActionsOut.push(addErrorNotice('Failed to update admin configuration.', error));
      if (recoveryError)
        epicActionsOut.push(
          addErrorNotice(
            'Something went really wrong while attempting to retrieve admin configuration, try refreshing the page.',
            recoveryError
          )
        );
      epicActionsOut.push(actions.syncDataResolve({ sync: status === SyncStatus.success, syncPatch }));
      return of(...epicActionsOut);
    })
  );

export const restartQestnet = (action$) =>
  action$.pipe(
    ofType(actions.restartQestnet.toString()),
    mergeMap(() =>
      callApi(webApi.admin.settings.restartQestnet.post()).pipe(
        // Test running a safe endpoint to see if the service is back.
        // Retry 3 times (4 times total). It takes awhile between timeouts to give the service a chance to return.
        // If there is a legitimate error it won't return.
        mergeMap(() => callApi(webApi.env.get())),
        retry(3),

        map(() => actions.restartQestnetResolve()),
        catchWithAuthErrors((err) => of(actions.restartQestnetReject({ error: errorToString(err) })))
      )
    )
  );

export const testEmailSettings = (action$) =>
  action$.pipe(
    ofType(actions.testEmailSettings.toString()),
    mergeMap(({ payload: { address: Address } = {} }) =>
      callApi(webApi.admin.settings.email.test.post({ Body: { Address } })).pipe(
        map(() => actions.testEmailSettingsResolve()),
        catchError((err) => of(actions.testEmailSettingsReject({ error: sanitizeErrorObject(err) })))
      )
    )
  );

export const testDatabaseConnections = (action$) =>
  action$.pipe(
    ofType(actions.testDatabaseConnection.toString()),
    switchMap(({ payload: { path: ConnectionName } = {} }) =>
      callApi(webApi.admin.settings.databaseConnections.test.post({ Body: { ConnectionName } })).pipe(
        map(() => actions.testDatabaseConnectionResolve({ path: ConnectionName })),
        catchError((err) =>
          of(actions.testDatabaseConnectionReject({ error: sanitizeErrorObject(err), path: ConnectionName }))
        )
      )
    )
  );

export const runDataIntegrator = (action$) =>
  action$.pipe(
    ofType(actions.triggerDataIntegratorEvent.toString()),
    switchMap(({ payload: { path, conditions, event, timeout } = {} }) =>
      callApi(
        webApi.admin.settings.dataIntegrators[event].post({
          Body: { IntegratorName: path, Conditions: conditions, WebApiTimeout: timeout },
        })
      ).pipe(
        mergeMap(() => of(actions.triggerDataIntegratorEventResolve({ path }), actions.syncData({ force: true }))),
        catchError((err) => of(actions.triggerDataIntegratorEventReject({ error: sanitizeErrorObject(err), path })))
      )
    )
  );

export const createEquipmentDriver = (action$) =>
  action$.pipe(
    ofType(actions.createEquipmentDriver.toString()),
    mergeMap(({ payload: { defaultEquipmentDriver } = {} }) =>
      callApi(webApi.equipment.drivers.query.post({ Body: defaultEquipmentDriver })).pipe(
        map((data) => actions.createEquipmentDriverResolve({ data })),
        catchWithAuthErrors((err) =>
          of(actions.createEquipmentDriverReject(), addErrorNotice('Failed to create the equipment driver.', err))
        )
      )
    )
  );

export const deleteEquipmentDriver = (action$) =>
  action$.pipe(
    ofType(actions.deleteEquipmentDriver.toString()),
    switchMap(({ payload: { elementId } = {} }) =>
      callApi(webApi.equipment.drivers.delete({ driverId: elementId })).pipe(
        mergeMap((result) => of(actions.deleteEquipmentDriverResolve({ result }), actions.syncData({ force: true }))),
        catchWithAuthErrors((err) =>
          of(actions.deleteEquipmentDriverReject(), addErrorNotice('Failed to delete the equipment driver.', err))
        )
      )
    )
  );

export const createAgsExportConfiguration = (action$) =>
  action$.pipe(
    ofType(actions.createAgsExportConfiguration.toString()),
    mergeMap(({ payload: { newAgsExportConfiguration } = {} }) =>
      callApi(webApi.ags.exportConfigurationList.post({ Body: newAgsExportConfiguration })).pipe(
        map((data) => actions.createAgsExportConfigurationResolve({ data })),
        catchWithAuthErrors((err) =>
          of(
            actions.createAgsExportConfigurationReject(),
            addErrorNotice('Failed to create the AGS Export Configuration.', err)
          )
        )
      )
    )
  );

export const deleteAgsExportConfiguration = (action$) =>
  action$.pipe(
    ofType(actions.deleteAgsExportConfiguration.toString()),
    switchMap(({ payload: { elementId } = {} }) =>
      callApi(webApi.ags.exportConfiguration.delete({ configurationId: elementId })).pipe(
        mergeMap((result) =>
          of(actions.deleteAgsExportConfigurationResolve({ result }), actions.syncData({ force: true }))
        ),
        catchWithAuthErrors((err) =>
          of(
            actions.deleteAgsExportConfigurationReject(),
            addErrorNotice('Failed to delete the AGS custom Configuration.', err)
          )
        )
      )
    )
  );

export const addAgsLaboratoryMapping = (action$) =>
  action$.pipe(
    ofType(actions.addAgsLaboratoryMapping.toString()),
    switchMap(({ payload: { labId, configurtionId } = {} }) =>
      callApi(
        webApi.ags.exportConfiguration.laboratoryList.post({
          Body: { LaboratoryUUID: labId, AgsExportConfigurationUUID: configurtionId },
        })
      ).pipe(
        mergeMap((result) => of(actions.addAgsLaboratoryMappingResolve({ result }), actions.syncData({ force: true }))),
        catchWithAuthErrors((err) =>
          of(actions.addAgsLaboratoryMappingReject(), addErrorNotice('Failed to add a new Laboratory mapping.', err))
        )
      )
    )
  );

export const setAgsLaboratoryMapping = (action$) =>
  action$.pipe(
    ofType(actions.setAgsLaboratoryMapping.toString()),
    switchMap(({ payload: { labId, configurtionId } = {} }) =>
      callApi(
        webApi.ags.exportConfiguration.laboratory.put(
          { Body: { AgsExportConfigurationUUID: configurtionId } },
          { laboratoryId: labId }
        )
      ).pipe(
        mergeMap((result) => of(actions.setAgsLaboratoryMappingResolve({ result }), actions.syncData({ force: true }))),
        catchWithAuthErrors((err) =>
          of(
            actions.setAgsLaboratoryMappingReject(),
            addErrorNotice('Failed to set the AGS Configuration for Laboratory.', err)
          )
        )
      )
    )
  );

export const setAgsDefaultConfigurationMapping = (action$) =>
  action$.pipe(
    ofType(actions.setAgsDefaultConfigurationMapping.toString()),
    switchMap(({ payload: { configurtionId } = {} }) =>
      callApi(
        webApi.ags.exportConfiguration.default.put({ Body: { AgsExportConfigurationUUID: configurtionId } }, {})
      ).pipe(
        mergeMap((result) =>
          of(actions.setAgsDefaultConfigurationMappingResolve({ result }), actions.syncData({ force: true }))
        ),
        catchWithAuthErrors((err) =>
          of(
            actions.setAgsDefaultConfigurationMappingReject(),
            addErrorNotice('Failed to set the default AGS Configuration.', err)
          )
        )
      )
    )
  );

export const deleteAgsDefaultConfigurationMapping = (action$) =>
  action$.pipe(
    ofType(actions.deleteAgsDefaultConfigurationMapping.toString()),
    switchMap(({ payload: { labId } = {} }) =>
      callApi(webApi.ags.exportConfiguration.laboratory.delete({ laboratoryId: labId })).pipe(
        mergeMap((result) =>
          of(actions.deleteAgsDefaultConfigurationMappingResolve({ result }), actions.syncData({ force: true }))
        ),
        catchWithAuthErrors((err) =>
          of(
            actions.deleteAgsDefaultConfigurationMappingReject(),
            addErrorNotice('Failed to delete AGS Configuration for Laboratory.', err)
          )
        )
      )
    )
  );

export const runNewAgsExport = (action$, state$) =>
  action$.pipe(
    ofType(actions.runNewAgsExport.toString()),
    switchMap(({ payload: { workOrderId, configurationId } = {} }) =>
      callApi(
        webApi.ags.exports.create.post({ Body: { WorkOrderId: workOrderId, ConfigurationId: configurationId } })
      ).pipe(
        map(() => actions.runNewAgsExportResolve()),
        catchWithAuthErrors((err) =>
          of(actions.runNewAgsExportReject(), addErrorNotice('Failed to create new AGS Export', err))
        )
      )
    )
  );

export const reportConfigurationSearch = (action$, state$) =>
  action$.pipe(
    ofType(actions.reportConfigSearchGet.toString()),
    switchMap(({ payload }) => {
      const filter = state$.value.admin.searchReportConfigs.filter;
      const queryParams = {
        configType: filter.configurationType,
        documentTypeIds: filter.documentTypes,
        reportTypeIds: filter.reportTypes,
        nameQuery: filter.nameQuery,
        includeSystem: filter.includeSystemConfigs,
      };
      return callApi(webApi.reportConfig.query.get(queryParams)).pipe(
        map((response) =>
          actions.reportConfigSearchGetResolve({
            configurations: response.Body.ReportConfigurations,
          })
        ),
        catchWithAuthErrors(() => of({ type: 'todo' }))
      );
    })
  );

export const createReportConfiguration = (action$) =>
  action$.pipe(
    ofType(actions.createReportConfiguration.toString()),
    mergeMap(({ payload: { newReportConfiguration } = {} }) =>
      callApi(webApi.reportConfig.create.post({ Body: newReportConfiguration })).pipe(
        map((data) => actions.createReportConfigurationResolve({ data })),
        catchWithAuthErrors((err) =>
          of(
            actions.createReportConfigurationReject(),
            addErrorNotice('Failed to create the Report Configuration.', err)
          )
        )
      )
    )
  );

export const deleteReportConfiguration = (action$) =>
  action$.pipe(
    ofType(actions.deleteReportConfiguration.toString()),
    switchMap(({ payload: { id } = {} }) =>
      callApi(webApi.reportConfig.delete({ reportConfigurationId: id })).pipe(
        mergeMap((result) =>
          of(actions.deleteReportConfigurationResolve({ result, id }), actions.syncData({ force: true }))
        ),
        catchWithAuthErrors((err) =>
          of(
            actions.deleteReportConfigurationReject(),
            addErrorNotice('Failed to delete the Report Configuration.', err)
          )
        )
      )
    )
  );

export const createReportConfigurationGroup = (action$) =>
  action$.pipe(
    ofType(actions.createReportConfigurationGroup.toString()),
    mergeMap(({ payload: { newReportConfigurationGroup } = {} }) =>
      callApi(webApi.reportConfigGroup.create.post({ Body: newReportConfigurationGroup })).pipe(
        map((data) => actions.createReportConfigurationGroupResolve({ data })),
        catchWithAuthErrors((err) =>
          of(
            actions.createReportConfigurationGroupReject(),
            addErrorNotice('Failed to create the Report Configuration Group.', err)
          )
        )
      )
    )
  );

export const deleteReportConfigurationGroup = (action$) =>
  action$.pipe(
    ofType(actions.deleteReportConfigurationGroup.toString()),
    switchMap(({ payload: { id } = {} }) =>
      callApi(webApi.reportConfigGroup.delete({ groupId: id })).pipe(
        mergeMap((result) =>
          of(actions.deleteReportConfigurationGroupResolve({ result, id }), actions.syncData({ force: true }))
        ),
        catchWithAuthErrors((err) =>
          of(
            actions.deleteReportConfigurationGroupReject(),
            addErrorNotice('Failed to delete the Report Configuration.', err)
          )
        )
      )
    )
  );
